import {useEffect, useRef} from "react";
import {useIsLoggedIn} from "UserProvider";
import {preloadAllPrivateRoutes} from "nav/preloadRoutes";
import {scheduleIdle} from "nav/scheduleIdle";

/**
 * Idle warm-up for the private-route chunks (#146). The moment the hydration-safe
 * {@link useIsLoggedIn} flag flips true (a returning session after mount, or a fresh sign-in), this
 * schedules a SINGLE background prefetch of all four private-route chunks, so the first open of
 * Transfers/Contacts/Reminders/Account is instant even if the user never hovered a nav link.
 *
 * The warm-up is deferred to {@link scheduleIdle} (`requestIdleCallback`, with a `setTimeout` fallback
 * for Safari, which has no rIC) so it never competes with the app's own post-login work. It fires at
 * most once per mount (a ref latch) and only when logged in — the data-saver guard lives inside
 * {@link preloadAllPrivateRoutes}. SSG-safe: `window` is touched only inside the post-mount effect.
 *
 * Epic #26: this is the logic seam — a component just calls the hook, it owns the "when/once" policy.
 */
export function usePrefetchPrivateRoutes(): void {
	const loggedIn = useIsLoggedIn();
	const warmed = useRef(false);
	useEffect(() => {
		if (!loggedIn || warmed.current) {
			return;
		}
		warmed.current = true;
		scheduleIdle(preloadAllPrivateRoutes);
	}, [loggedIn]);
}
